import { useEffect, useMemo, useState } from "react";
import { buildVenueAddressQuery, enrichVisitDistances } from "helpers/geocode/meetPlaceDistance";

const visitDistanceKey = (visit, venue) => {
  if (!visit) return "";
  return JSON.stringify({
    venue: buildVenueAddressQuery(venue),
    hotels: (visit.hotels || []).map((p) => [p.name, p.address, p.distance]),
  });
};

/**
 * Fills in driving `distance` on visit hotels from `address` + venue.
 * Restaurants keep directions only — no distance shown.
 */
const useVisitPlaceDistances = (visit, venue) => {
  const [resolvedVisit, setResolvedVisit] = useState(visit);
  const [distancesLoading, setDistancesLoading] = useState(false);
  const cacheKey = useMemo(() => visitDistanceKey(visit, venue), [visit, venue]);

  useEffect(() => {
    setResolvedVisit(visit);
  }, [visit]);

  useEffect(() => {
    if (!visit) return undefined;

    const needsHotels = visit.hotels?.some((p) => p.address && !p.distance);
    if (!needsHotels) return undefined;

    let cancelled = false;
    setDistancesLoading(true);

    enrichVisitDistances(visit, venue)
      .then((next) => {
        if (!cancelled) setResolvedVisit(next);
      })
      .finally(() => {
        if (!cancelled) setDistancesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, visit, venue]);

  return { visit: resolvedVisit, distancesLoading };
};

export default useVisitPlaceDistances;
